import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatRupiah, priceInfo } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { applyAccent, ACCENT_PRESETS } from "@/lib/theme";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import AdminChat from "@/components/AdminChat";
import { statusOf } from "@/lib/orderStatus";

import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Loader2, ShoppingBag, Wallet, Clock, CheckCircle2, Plus, Trash2, Pencil, Upload, X,
  Printer, Truck, TrendingUp, Newspaper, FileText, Flame, Download, ArrowUp, ArrowDown, Palette, MapPin, Layers, ImagePlus, Eye,
} from "lucide-react";

const ORDER_STATUSES = [
  { value: "menunggu_pembayaran", label: "Menunggu Pembayaran" },
  { value: "diproses", label: "Diproses" },
  { value: "dikirim", label: "Sudah Dikirim" },
  { value: "selesai", label: "Selesai" },
  { value: "dibatalkan", label: "Dibatalkan" },
];
const PRODUCT_TYPES = [
  { value: "mug", label: "Gelas / Mug" },
  { value: "plaque", label: "Plakat Akrilik" },
  { value: "custom", label: "Produk Lain" },
];
const REPORT_RANGES = [
  { value: "today", label: "Hari Ini" },
  { value: "7d", label: "7 Hari" },
  { value: "month", label: "Bulan Ini" },
  { value: "30d", label: "30 Hari" },
];
const WEIGHTS = { mug: 600, plaque: 1000, custom: 500 };
const SENDER = {
  name: "Rooftop Print",
  phone: "081314092016",
  address: "Jl. Pagarsih No.122, Cibadak, Kec. Astanaanyar, Kota Bandung, Jawa Barat 40241",
};

async function uploadImage(file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return `${api.defaults.baseURL}/files/${res.data.file_id}`;
}

function ImageUploader({ value, onChange, testid }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try { onChange(await uploadImage(f)); }
    catch (err) { toast.error("Gagal mengunggah gambar"); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={pick} data-testid={testid} />
      {value ? (
        <div className="flex items-center gap-3">
          <img src={value} alt="" className="w-16 h-16 rounded-xl object-cover border border-slate-200" />
          <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => ref.current?.click()} disabled={busy}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ganti Foto"}
          </Button>
        </div>
      ) : (
        <button type="button" onClick={() => ref.current?.click()} disabled={busy}
          className="w-full border-2 border-dashed border-slate-300 rounded-xl p-5 flex items-center justify-center gap-2 hover:border-[#F0005A] transition-colors text-sm text-slate-500">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4" /> Unggah foto produk</>}
        </button>
      )}
    </div>
  );
}

const EMPTY_PRODUCT = { name: "", type: "custom", product_kind: "custom", base_price: "", promo_price: "", weight: 500, min_order: 1, preorder: false, preorder_days: "", description: "", image_url: "", images: [], video_url: "", variant_groups: [], variant_combinations: [], active: true, best_seller: false };

function VariantPhoto({ value, onChange, testid }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try { onChange(await uploadImage(f)); } catch { toast.error("Gagal mengunggah foto varian"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div>
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={pick} data-testid={testid} />
      <button type="button" onClick={() => ref.current?.click()} disabled={busy}
        className="w-14 h-14 rounded-lg border-2 border-dashed border-slate-300 hover:border-[#F0005A] flex items-center justify-center overflow-hidden bg-slate-50 shrink-0 transition-colors">
        {busy ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : value ? <img src={value} alt="" className="w-full h-full object-cover" /> : <Plus className="w-4 h-4 text-slate-400" />}
      </button>
    </div>
  );
}

const COMBO_SEP = "\u241E";
function generateCombos(groups, existing) {
  const valid = groups.filter((g) => g.name && g.options.some((o) => o.label.trim()));
  if (!valid.length) return [];
  const lists = valid.map((g) => g.options.map((o) => o.label.trim()).filter(Boolean));
  if (lists.some((l) => l.length === 0)) return [];
  let combos = [[]];
  lists.forEach((list) => { const nx = []; combos.forEach((c) => list.forEach((l) => nx.push([...c, l]))); combos = nx; });
  const exMap = {};
  (existing || []).forEach((c) => { exMap[(c.labels || []).join(COMBO_SEP)] = c; });
  return combos.map((labels) => {
    const prev = exMap[labels.join(COMBO_SEP)] || {};
    return { labels, image_url: prev.image_url || "", price: prev.price ?? "", promo_price: prev.promo_price ?? "", stock: prev.stock ?? "", weight: prev.weight ?? "" };
  });
}

function BulkComboDialog({ open, onOpenChange, onApply }) {
  const [price, setPrice] = useState("");
  const [discount, setDiscount] = useState("");
  const [promo, setPromo] = useState("");
  const [stock, setStock] = useState("");
  useEffect(() => { if (open) { setPrice(""); setDiscount(""); setPromo(""); setStock(""); } }, [open]);
  const apply = () => {
    const patch = {};
    if (price !== "") patch.price = price;
    if (promo !== "") patch.promo_price = promo;
    if (stock !== "") patch.stock = stock;
    const discountPct = discount !== "" ? Number(discount) : null;
    if (!Object.keys(patch).length && discountPct == null) { toast.error("Isi minimal satu kolom"); return; }
    onApply(patch, discountPct); onOpenChange(false); toast.success("Diterapkan ke semua kombinasi");
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Ubah Massal Kombinasi</DialogTitle>
          <DialogDescription>Terapkan nilai ke SEMUA baris kombinasi sekaligus. Kolom yang dikosongkan tidak diubah. Diskon % akan menghitung Harga Promo tiap baris dari Harga Normal-nya masing-masing.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-sm">Harga Normal (Rp)</Label><Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="mis. 25000" className="mt-1.5 rounded-xl" data-testid="bulk-combo-price" /></div>
          <div><Label className="text-sm">Diskon (%) — hitung Promo otomatis per baris</Label><Input type="number" value={discount} onChange={(e) => setDiscount(e.target.value)} placeholder="mis. 20" className="mt-1.5 rounded-xl" data-testid="bulk-combo-discount" /></div>
          <div><Label className="text-sm">Harga Promo (Rp) — abaikan jika pakai Diskon %</Label><Input type="number" value={promo} onChange={(e) => setPromo(e.target.value)} placeholder="mis. 20000" className="mt-1.5 rounded-xl" data-testid="bulk-combo-promo" /></div>
          <div><Label className="text-sm">Stok</Label><Input type="number" value={stock} onChange={(e) => setStock(e.target.value)} placeholder="mis. 100 (0 = habis)" className="mt-1.5 rounded-xl" data-testid="bulk-combo-stock" /></div>
        </div>
        <DialogFooter>
          <Button onClick={apply} className="rounded-full bg-[#F0005A] text-white" data-testid="bulk-combo-apply">Terapkan ke Semua</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProductFormDialog({ open, onOpenChange, product, onSaved }) {
  const editing = !!product;
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [saving, setSaving] = useState(false);
  const [bulkCombos, setBulkCombos] = useState(false);

  useEffect(() => {
    if (open) setForm(product ? {
      ...EMPTY_PRODUCT, ...product, promo_price: product.promo_price || "",
      images: product.images || (product.image_url ? [product.image_url] : []),
      variant_groups: (product.variant_groups || []).map((g) => ({ name: g.name, options: (g.options || []).map((o) => ({ label: o.label })) })),
      variant_combinations: product.variant_combinations || [],
    } : EMPTY_PRODUCT);
  }, [open, product]);

  const groupsSig = JSON.stringify(form.variant_groups.map((g) => ({ n: g.name, o: g.options.map((o) => o.label) })));
  useEffect(() => {
    setForm((f) => ({ ...f, variant_combinations: generateCombos(f.variant_groups, f.variant_combinations) }));
  }, [groupsSig]); // eslint-disable-line

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const uploadVideo = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 20 * 1024 * 1024) { toast.error("Ukuran video maksimal 20MB"); e.target.value = ""; return; }
    const fd = new FormData(); fd.append("file", f);
    try {
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("video_url", `${api.defaults.baseURL}/files/${r.data.file_id}`);
      toast.success("Video terunggah");
    } catch (err) { toast.error("Gagal mengunggah video"); }
    e.target.value = "";
  };
  const addImage = (url) => setForm((f) => ({ ...f, images: [...f.images, url] }));
  const removeImage = (i) => setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));
  const addGroup = () => setForm((f) => f.variant_groups.length >= 2 ? f : { ...f, variant_groups: [...f.variant_groups, { name: "", options: [{ label: "" }] }] });
  const removeGroup = (gi) => setForm((f) => ({ ...f, variant_groups: f.variant_groups.filter((_, i) => i !== gi) }));
  const setGroupName = (gi, v) => setForm((f) => ({ ...f, variant_groups: f.variant_groups.map((g, i) => i === gi ? { ...g, name: v } : g) }));
  const addOption = (gi) => setForm((f) => ({ ...f, variant_groups: f.variant_groups.map((g, i) => i === gi ? { ...g, options: [...g.options, { label: "" }] } : g) }));
  const removeOption = (gi, oi) => setForm((f) => ({ ...f, variant_groups: f.variant_groups.map((g, i) => i === gi ? { ...g, options: g.options.filter((_, j) => j !== oi) } : g) }));
  const setOptionLabel = (gi, oi, v) => setForm((f) => ({ ...f, variant_groups: f.variant_groups.map((g, i) => i === gi ? { ...g, options: g.options.map((o, j) => j === oi ? { label: v } : o) } : g) }));
  const setCombo = (idx, k, v) => setForm((f) => ({ ...f, variant_combinations: f.variant_combinations.map((c, i) => i === idx ? { ...c, [k]: v } : c) }));
  const setComboDiscount = (idx, pct) => setForm((f) => ({ ...f, variant_combinations: f.variant_combinations.map((c, i) => {
    if (i !== idx) return c;
    const price = Number(c.price) || 0;
    const p = Number(pct);
    const promo = (p > 0 && price > 0) ? String(Math.round(price * (1 - p / 100))) : "";
    return { ...c, promo_price: promo };
  }) }));
  const bulkApplyCombos = (patch, discountPct) => setForm((f) => ({ ...f, variant_combinations: f.variant_combinations.map((c) => {
    const next = { ...c, ...patch };
    if (discountPct != null) {
      const price = Number(next.price) || 0;
      next.promo_price = (discountPct > 0 && price > 0) ? String(Math.round(price * (1 - discountPct / 100))) : "";
    }
    return next;
  }) }));

  const save = async () => {
    if (!form.name || form.images.length === 0) { toast.error("Nama dan minimal 1 foto produk wajib diisi"); return; }
    const hasCombos = (form.variant_combinations || []).length > 0;
    if (!hasCombos && !form.base_price) { toast.error("Harga produk wajib diisi"); return; }
    if (hasCombos && form.variant_combinations.some((c) => !(Number(c.price) > 0))) { toast.error("Setiap baris kombinasi wajib memiliki Harga Normal lebih dari 0"); return; }
    setSaving(true);
    try {
      const combosOut = (form.variant_combinations || []).filter((c) => c.labels && c.labels.length).map((c) => ({
        labels: c.labels, image_url: c.image_url || "", price: Number(c.price) || 0,
        promo_price: (c.promo_price === "" || c.promo_price == null) ? null : (Number(c.promo_price) || 0),
        stock: (c.stock === "" || c.stock == null) ? null : (Number(c.stock) || 0),
        weight: (c.weight === "" || c.weight == null) ? null : (Number(c.weight) || 0),
      }));
      const minComboPrice = combosOut.length ? Math.min(...combosOut.map((c) => (c.promo_price && c.promo_price > 0 && c.promo_price < c.price) ? c.promo_price : c.price)) : 0;
      const payload = {
        name: form.name, type: form.type, product_kind: form.product_kind,
        base_price: combosOut.length ? minComboPrice : Number(form.base_price),
        promo_price: combosOut.length ? 0 : (form.promo_price ? Number(form.promo_price) : 0),
        weight: Number(form.weight) || 500, min_order: Math.max(1, Number(form.min_order) || 1),
        preorder: form.preorder, preorder_days: form.preorder_days,
        description: form.description, image_url: form.images[0], images: form.images, video_url: form.video_url || "",
        variant_groups: form.variant_groups.filter((g) => g.name && g.options.some((o) => o.label.trim())).map((g) => ({ name: g.name, options: g.options.filter((o) => o.label.trim()).map((o) => ({ label: o.label.trim() })) })),
        variant_combinations: combosOut,
        active: form.active, best_seller: form.best_seller,
      };
      if (editing) await api.put(`/products/${product.id}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Produk diperbarui" : "Produk ditambahkan");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error("Gagal menyimpan produk"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="left-0 top-0 translate-x-0 translate-y-0 max-w-none w-screen h-screen rounded-none overflow-y-auto sm:p-8" data-testid="product-form-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit ${product.name}` : "Tambah Produk Baru"}</DialogTitle>
          <DialogDescription>Atur foto galeri, harga & promo, varian matriks (foto/harga/stok per kombinasi), pre-order, dan minimal order.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-w-4xl mx-auto w-full">
          <div>
            <Label className="text-sm">Galeri Foto Produk</Label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {form.images.map((img, i) => (
                <div key={i} className="relative w-16 h-16">
                  <img src={img} alt="" className="w-16 h-16 rounded-xl object-cover border border-slate-200" />
                  <button onClick={() => removeImage(i)} className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"><X className="w-3 h-3" /></button>
                </div>
              ))}
              <div className="w-40"><ImageUploader value="" onChange={addImage} testid="product-image-input" /></div>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="text-sm">Nama Produk</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5 rounded-xl" data-testid="product-name-input" /></div>
            <div>
              <Label className="text-sm">Jenis Produk</Label>
              <Select value={form.product_kind} onValueChange={(v) => set("product_kind", v)}>
                <SelectTrigger className="mt-1.5 rounded-xl" data-testid="product-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="custom">Custom (ada unggah desain)</SelectItem><SelectItem value="ready">Ready Stok (tanpa unggah)</SelectItem></SelectContent>
              </Select>
            </div>
            {form.variant_combinations.length === 0 && (
              <>
                <div><Label className="text-sm">Harga (Rp)</Label><Input type="number" value={form.base_price} onChange={(e) => set("base_price", e.target.value)} className="mt-1.5 rounded-xl" data-testid="product-price-input" /></div>
                <div><Label className="text-sm">Berat (gram)</Label><Input type="number" value={form.weight} onChange={(e) => set("weight", e.target.value)} className="mt-1.5 rounded-xl" data-testid="product-weight-input" /></div>
              </>
            )}
            <div><Label className="text-sm">Min. Pemesanan (pcs)</Label><Input type="number" value={form.min_order} onChange={(e) => set("min_order", e.target.value)} className="mt-1.5 rounded-xl" data-testid="product-minorder-input" /></div>
          </div>
          <div><Label className="text-sm">Deskripsi</Label><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1.5 rounded-xl" data-testid="product-desc-input" /></div>
          <div>
            <Label className="text-sm">Video Produk (opsional, maks 20MB · portrait 9:16 disarankan)</Label>
            <div className="mt-1.5 flex items-center gap-3">
              <input type="file" accept="video/*" onChange={uploadVideo} data-testid="product-video-input"
                className="text-sm file:mr-3 file:rounded-full file:border-0 file:bg-[#00133F] file:text-white file:px-4 file:py-2 file:cursor-pointer" />
              {form.video_url && (
                <div className="flex items-center gap-2">
                  <video src={form.video_url} className="w-12 h-16 object-cover rounded-lg bg-black" muted />
                  <button type="button" onClick={() => set("video_url", "")} className="text-slate-400 hover:text-red-500" data-testid="remove-video-btn"><X className="w-4 h-4" /></button>
                </div>
              )}
            </div>
          </div>

          {/* Pre-order */}
          <div className="rounded-xl border border-slate-200 p-4 space-y-3">
            <div className="flex items-center justify-between"><Label className="text-sm">Status Pre-Order</Label><Switch checked={form.preorder} onCheckedChange={(v) => set("preorder", v)} data-testid="preorder-switch" /></div>
            {form.preorder && <div><Label className="text-sm">Durasi Proses (hari kerja)</Label><Input value={form.preorder_days} onChange={(e) => set("preorder_days", e.target.value)} placeholder="mis. 3-5" className="mt-1.5 rounded-xl" data-testid="preorder-days-input" /></div>}
          </div>

          {/* Variant matrix (max 2 categories) */}
          <div>
            <div className="flex items-start justify-between gap-2">
              <div>
                <Label className="text-sm">Varian (maks. 2 kategori)</Label>
                <p className="text-xs text-slate-400 mt-0.5">Langkah 1: isi nama kategori & pilihannya. Langkah 2: tabel kombinasi otomatis muncul di bawah — isi Foto, Harga Normal, Diskon/Promo, Stok & Berat tiap baris.</p>
              </div>
              <Button type="button" size="sm" variant="outline" className="rounded-full h-8 shrink-0" onClick={addGroup} disabled={form.variant_groups.length >= 2} data-testid="add-variant-group-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Kategori</Button>
            </div>

            {/* Stage 1: categories & options */}
            <div className="grid sm:grid-cols-2 gap-3 mt-2">
              {form.variant_groups.map((g, gi) => (
                <div key={gi} className="rounded-xl border border-slate-200 p-3" data-testid={`variant-group-row-${gi}`}>
                  <div className="flex gap-2 items-center">
                    <Input placeholder={`Nama kategori ${gi + 1} (mis. Warna)`} value={g.name} onChange={(e) => setGroupName(gi, e.target.value)} className="rounded-xl h-9" data-testid={`variant-group-name-${gi}`} />
                    <button type="button" onClick={() => removeGroup(gi)} className="text-slate-400 hover:text-red-500 p-2 shrink-0"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <div className="space-y-2 mt-2">
                    {g.options.map((o, oi) => (
                      <div key={oi} className="flex gap-2 items-center">
                        <Input placeholder={`Pilihan ${oi + 1} (mis. Hitam)`} value={o.label} onChange={(e) => setOptionLabel(gi, oi, e.target.value)} className="rounded-xl h-9 flex-1" data-testid={`variant-option-label-${gi}-${oi}`} />
                        <button type="button" onClick={() => removeOption(gi, oi)} className="text-slate-400 hover:text-red-500 p-1 shrink-0"><X className="w-4 h-4" /></button>
                      </div>
                    ))}
                    <Button type="button" size="sm" variant="ghost" className="rounded-full h-7 text-xs" onClick={() => addOption(gi)} data-testid={`add-option-${gi}`}><Plus className="w-3 h-3 mr-1" /> Pilihan</Button>
                  </div>
                </div>
              ))}
              {form.variant_groups.length === 0 && <p className="text-xs text-slate-400 sm:col-span-2">Belum ada varian. Produk dijual satu harga (pakai Harga Normal/Promo di atas).</p>}
            </div>

            {/* Stage 2: combination matrix table */}
            {form.variant_combinations.length > 0 && (
              <div className="mt-4" data-testid="variant-combos-table">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm">Tabel Kombinasi ({form.variant_combinations.length} baris)</Label>
                  <Button type="button" size="sm" variant="outline" className="rounded-full h-8" onClick={() => setBulkCombos(true)} data-testid="bulk-edit-combos-btn"><Layers className="w-3.5 h-3.5 mr-1" /> Ubah Massal</Button>
                </div>
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-[11px] font-medium text-slate-500">
                      <tr>
                        <th className="p-2 text-left w-16">Foto</th>
                        {form.variant_groups.filter((g) => g.name).map((g, i) => <th key={i} className="p-2 text-left whitespace-nowrap">{g.name}</th>)}
                        <th className="p-2 text-left w-28">Harga Normal (Rp)</th>
                        <th className="p-2 text-left w-24">Diskon (%)</th>
                        <th className="p-2 text-left w-28">Harga Promo (Rp)</th>
                        <th className="p-2 text-left w-20">Stok</th>
                        <th className="p-2 text-left w-24">Berat (g)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.variant_combinations.map((c, idx) => (
                        <tr key={idx} className="border-t border-slate-100" data-testid={`combo-row-${idx}`}>
                          <td className="p-2"><VariantPhoto value={c.image_url || ""} onChange={(url) => setCombo(idx, "image_url", url)} testid={`combo-photo-${idx}`} /></td>
                          {c.labels.map((l, li) => <td key={li} className="p-2 font-medium text-[#00133F] whitespace-nowrap">{l}</td>)}
                          <td className="p-2"><Input type="number" value={c.price} onChange={(e) => setCombo(idx, "price", e.target.value)} placeholder="0" className="rounded-lg h-9" data-testid={`combo-price-${idx}`} /></td>
                          <td className="p-2"><Input type="number" value={c.price && c.promo_price && Number(c.promo_price) > 0 && Number(c.promo_price) < Number(c.price) ? Math.round((1 - Number(c.promo_price) / Number(c.price)) * 100) : ""} onChange={(e) => setComboDiscount(idx, e.target.value)} placeholder="—" className="rounded-lg h-9" data-testid={`combo-discount-${idx}`} /></td>
                          <td className="p-2"><Input type="number" value={c.promo_price ?? ""} onChange={(e) => setCombo(idx, "promo_price", e.target.value)} placeholder="—" className="rounded-lg h-9" data-testid={`combo-promo-${idx}`} /></td>
                          <td className="p-2"><Input type="number" value={c.stock ?? ""} onChange={(e) => setCombo(idx, "stock", e.target.value)} placeholder="∞" className="rounded-lg h-9" data-testid={`combo-stock-${idx}`} /></td>
                          <td className="p-2"><Input type="number" value={c.weight ?? ""} onChange={(e) => setCombo(idx, "weight", e.target.value)} placeholder="500" className="rounded-lg h-9" data-testid={`combo-weight-${idx}`} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-[11px] text-slate-400 mt-2">Isi <strong>Diskon %</strong> → Harga Promo terhitung otomatis; atau isi <strong>Harga Promo</strong> → Diskon % muncul otomatis. Kosongkan Stok = tak terbatas; isi <strong>0</strong> = habis. Berat dipakai untuk hitung ongkir.</p>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between"><Label className="text-sm">Tampilkan di katalog</Label><Switch checked={form.active} onCheckedChange={(v) => set("active", v)} /></div>
          <div className="flex items-center justify-between"><Label className="text-sm">Tandai sebagai Best Seller</Label><Switch checked={form.best_seller} onCheckedChange={(v) => set("best_seller", v)} data-testid="best-seller-switch" /></div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} className="rounded-full bg-[#F0005A] text-white" data-testid="save-product-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Produk"}
          </Button>
        </DialogFooter>
        <BulkComboDialog open={bulkCombos} onOpenChange={setBulkCombos} onApply={bulkApplyCombos} />
      </DialogContent>
    </Dialog>
  );
}

function StorefrontManager() {
  const [sf, setSf] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/settings/storefront").then((r) => setSf(r.data)); }, []);
  if (!sf) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#F0005A]" /></div>;
  const hero = sf.hero || {};
  const banners = sf.banners || [];
  const accent = sf.theme?.accent || "#F0005A";
  const setHero = (k, v) => setSf((s) => ({ ...s, hero: { ...s.hero, [k]: v } }));
  const setAccent = (v) => { setSf((s) => ({ ...s, theme: { ...(s.theme || {}), accent: v } })); applyAccent(v); };
  const addBanner = (url) => { if (!url) return; setSf((s) => ({ ...s, banners: [...(s.banners || []), { id: String(Date.now()), image_url: url }] })); };
  const removeBanner = (i) => setSf((s) => ({ ...s, banners: s.banners.filter((_, idx) => idx !== i) }));
  const move = (i, dir) => setSf((s) => { const b = [...s.banners]; const j = i + dir; if (j < 0 || j >= b.length) return s; [b[i], b[j]] = [b[j], b[i]]; return { ...s, banners: b }; });
  const save = async () => {
    if (!hero.title || !hero.subtitle) { toast.error("Judul & sub-judul wajib diisi"); return; }
    if (!banners.length) { toast.error("Tambahkan minimal 1 banner"); return; }
    setSaving(true);
    try {
      await api.put("/admin/settings/storefront", { hero, banners, theme: { accent } });
      applyAccent(accent);
      toast.success("Dekorasi toko diterapkan. Buka halaman utama untuk melihat.");
    } catch (e) { toast.error("Gagal menyimpan"); } finally { setSaving(false); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-8" data-testid="storefront-manager">
      <div className="space-y-6">
        {/* Banners */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-[#00133F]">Banner Slider</h3>
            <span className="text-xs text-slate-400">{banners.length} banner</span>
          </div>
          <div className="space-y-3">
            {banners.map((b, i) => (
              <div key={b.id || i} className="flex items-center gap-3 rounded-xl border border-slate-200 p-2" data-testid={`banner-row-${i}`}>
                <img src={b.image_url} alt="" className="w-20 h-14 rounded-lg object-cover shrink-0" />
                <span className="text-xs text-slate-400 flex-1 truncate">Banner #{i + 1}</span>
                <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1.5 text-slate-400 hover:text-[#00133F] disabled:opacity-30" data-testid={`banner-up-${i}`}><ArrowUp className="w-4 h-4" /></button>
                <button onClick={() => move(i, 1)} disabled={i === banners.length - 1} className="p-1.5 text-slate-400 hover:text-[#00133F] disabled:opacity-30" data-testid={`banner-down-${i}`}><ArrowDown className="w-4 h-4" /></button>
                <button onClick={() => removeBanner(i)} className="p-1.5 text-slate-400 hover:text-red-500" data-testid={`banner-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
          <div className="mt-3">
            <Label className="text-sm">Tambah Banner Baru</Label>
            <div className="mt-1.5"><ImageUploader value="" onChange={addBanner} testid="add-banner-input" /></div>
          </div>
        </div>

        {/* Hero text */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
          <h3 className="font-heading font-semibold text-[#00133F]">Teks Utama (Hero)</h3>
          <div><Label className="text-sm">Judul Utama</Label><Textarea value={hero.title || ""} onChange={(e) => setHero("title", e.target.value)} className="mt-1.5 rounded-xl" data-testid="hero-title-input" /></div>
          <div><Label className="text-sm">Sub-judul</Label><Textarea value={hero.subtitle || ""} onChange={(e) => setHero("subtitle", e.target.value)} className="mt-1.5 rounded-xl" data-testid="hero-subtitle-input" /></div>
          <div><Label className="text-sm">Teks Tombol</Label><Input value={hero.cta_text || ""} onChange={(e) => setHero("cta_text", e.target.value)} className="mt-1.5 rounded-xl" data-testid="hero-cta-input" /></div>
        </div>

        {/* Theme color */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="flex items-center gap-2 mb-4"><Palette className="w-5 h-5 text-[#F0005A]" /><h3 className="font-heading font-semibold text-[#00133F]">Warna Aksen Tema</h3></div>
          <div className="flex flex-wrap gap-2 mb-4">
            {ACCENT_PRESETS.map((p) => (
              <button key={p.value} onClick={() => setAccent(p.value)} title={p.name} data-testid={`accent-preset-${p.value.replace('#','')}`}
                className={`w-9 h-9 rounded-full border-2 transition-transform hover:scale-110 ${accent.toLowerCase() === p.value.toLowerCase() ? "border-slate-800 ring-2 ring-offset-2 ring-slate-300" : "border-white shadow"}`}
                style={{ backgroundColor: p.value }} />
            ))}
          </div>
          <div className="flex items-center gap-3">
            <input type="color" value={accent} onChange={(e) => setAccent(e.target.value)} className="w-12 h-10 rounded-lg border border-slate-200 cursor-pointer" data-testid="accent-color-picker" />
            <span className="text-sm text-slate-500 font-mono">{accent.toUpperCase()}</span>
          </div>
        </div>

        <Button onClick={save} disabled={saving} className="rounded-full bg-[#F0005A] text-white h-11 px-6" data-testid="save-storefront-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan & Terapkan"}
        </Button>
      </div>

      {/* Preview */}
      <div>
        <p className="text-sm text-slate-500 mb-3">Pratinjau Beranda</p>
        <div className="rounded-2xl border border-slate-200 overflow-hidden bg-white">
          {banners[0] && <img src={banners[0].image_url} alt="" className="w-full h-52 object-cover" />}
          <div className="p-6">
            <h3 className="font-heading text-2xl font-bold text-[#00133F] whitespace-pre-line">{hero.title}</h3>
            <p className="text-sm text-slate-500 mt-2 whitespace-pre-line">{hero.subtitle}</p>
            <span className="inline-block mt-4 px-5 py-2 rounded-full text-white text-sm" style={{ backgroundColor: accent }}>{hero.cta_text || "Mulai Desain"}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const COURIER_OPTIONS = [
  { code: "jne", name: "JNE" }, { code: "jnt", name: "J&T Express" }, { code: "sicepat", name: "SiCepat" },
  { code: "pos", name: "POS Indonesia" }, { code: "tiki", name: "TIKI" }, { code: "anteraja", name: "AnterAja" },
  { code: "sap", name: "SAP Express" }, { code: "ninja", name: "Ninja Xpress" }, { code: "wahana", name: "Wahana" },
  { code: "lion", name: "Lion Parcel" }, { code: "ide", name: "ID Express" }, { code: "ncs", name: "NCS" },
];

function CourierManager() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/settings/shipping-config").then((r) => setCfg(r.data)); }, []);
  if (!cfg) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#F0005A]" /></div>;
  const enabled = new Set(cfg.couriers || []);
  const toggle = (code) => setCfg((c) => { const s = new Set(c.couriers || []); s.has(code) ? s.delete(code) : s.add(code); return { ...c, couriers: [...s] }; });
  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/settings/shipping-config", { couriers: cfg.couriers || [], instant_enabled: !!cfg.instant_enabled }); toast.success("Pengaturan kurir disimpan"); }
    catch (e) { toast.error("Gagal menyimpan kurir"); } finally { setSaving(false); }
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 mt-8" data-testid="courier-manager">
      <div className="flex items-center gap-2 mb-1"><Truck className="w-5 h-5 text-[#F0005A]" /><h3 className="font-heading font-semibold text-[#00133F]">Pilihan Kurir Pengiriman</h3></div>
      <p className="text-sm text-slate-500 mb-4">Centang kurir yang ingin ditampilkan ke pembeli saat checkout. (Ketersediaan tarif bergantung pada akun RajaOngkir Anda.)</p>
      <div className="grid sm:grid-cols-3 gap-3">
        {COURIER_OPTIONS.map((c) => (
          <div key={c.code} role="button" tabIndex={0} onClick={() => toggle(c.code)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(c.code); } }}
            className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors select-none ${enabled.has(c.code) ? "border-[#F0005A] bg-[#F0005A]/5" : "border-slate-200 hover:border-slate-300"}`} data-testid={`courier-toggle-${c.code}`}>
            <Checkbox checked={enabled.has(c.code)} className="pointer-events-none data-[state=checked]:bg-[#F0005A] data-[state=checked]:border-[#F0005A]" />
            <span className="text-sm font-medium text-[#00133F]">{c.name}</span>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between mt-5 p-4 rounded-xl bg-slate-50 border border-slate-100">
        <div>
          <p className="text-sm font-medium text-[#00133F]">Kurir Instan (Gojek / Grab / Lalamove)</p>
          <p className="text-xs text-slate-500 mt-0.5">Biaya kirim dibayar oleh pembeli saat barang tiba.</p>
        </div>
        <Switch checked={!!cfg.instant_enabled} onCheckedChange={(v) => setCfg((c) => ({ ...c, instant_enabled: v }))} data-testid="instant-courier-switch" />
      </div>
      <Button onClick={save} disabled={saving} className="rounded-full bg-[#00133F] text-white mt-5" data-testid="save-courier-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Pengaturan Kurir"}
      </Button>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, onClick, active }) {
  return (
    <button type="button" onClick={onClick} data-testid={`stat-card-${label}`}
      className={`text-left w-full rounded-2xl border bg-white p-6 transition-all hover:shadow-lg hover:-translate-y-0.5 ${active ? "border-[#F0005A] ring-2 ring-[#F0005A]/30" : "border-slate-200"}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-500">{label}</p>
          <p className="font-heading text-2xl font-bold text-[#00133F] mt-1">{value}</p>
        </div>
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${color}`}><Icon className="w-5 h-5 text-white" /></div>
      </div>
    </button>
  );
}

function SalesReport() {
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/report?period=${range}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [range]);

  const maxRev = data?.series?.length ? Math.max(...data.series.map((s) => s.revenue), 1) : 1;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 mb-8" data-testid="sales-report">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-[#00133F] flex items-center justify-center"><TrendingUp className="w-4 h-4 text-[#F0005A]" /></div>
          <h2 className="font-heading font-semibold text-lg text-[#00133F]">Laporan Penjualan</h2>
        </div>
        <Select value={range} onValueChange={setRange}>
          <SelectTrigger className="rounded-full w-40" data-testid="report-range-select"><SelectValue /></SelectTrigger>
          <SelectContent>{REPORT_RANGES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {loading || !data ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#F0005A]" /></div>
      ) : (
        <>
          <div className="grid sm:grid-cols-3 gap-4 mb-6">
            <div className="rounded-xl bg-slate-50 p-4">
              <p className="text-xs text-slate-500">Total Pesanan</p>
              <p className="font-heading text-xl font-bold text-[#00133F] mt-1" data-testid="report-total-orders">{data.total_orders}</p>
            </div>
            <div className="rounded-xl bg-slate-50 p-4">
              <p className="text-xs text-slate-500">Total Pendapatan</p>
              <p className="font-heading text-xl font-bold text-[#F0005A] mt-1" data-testid="report-total-revenue">{formatRupiah(data.total_revenue)}</p>
            </div>
            <div className="rounded-xl bg-slate-50 p-4">
              <p className="text-xs text-slate-500">Pesanan Lunas</p>
              <p className="font-heading text-xl font-bold text-emerald-600 mt-1">{data.paid_orders}</p>
            </div>
          </div>
          {data.series.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">Belum ada data pada periode ini.</p>
          ) : (
            <div className="flex items-end gap-1.5 h-32 overflow-x-auto pt-2">
              {data.series.map((s) => (
                <div key={s.date} className="flex flex-col items-center gap-1 min-w-[26px] flex-1" title={`${s.date}: ${formatRupiah(s.revenue)} · ${s.orders} pesanan`}>
                  <div className="w-full bg-[#F0005A]/80 rounded-t-md transition-all hover:bg-[#F0005A]"
                    style={{ height: `${Math.max(4, (s.revenue / maxRev) * 100)}%` }} />
                  <span className="text-[9px] text-slate-400 whitespace-nowrap">{s.date.slice(5)}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ShippingLabelDialog({ order, open, onOpenChange, sender }) {
  if (!order) return null;
  const S = sender || SENDER;
  const totalWeight = order.items.reduce((s, i) => s + (WEIGHTS[i.product_type] || 500) * i.quantity, 0);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader className="no-print">
          <DialogTitle>Label Pengiriman</DialogTitle>
          <DialogDescription>Pratinjau label. Klik cetak untuk mencetak atau simpan sebagai PDF.</DialogDescription>
        </DialogHeader>

        <div id="shipping-label" className="border-2 border-black rounded-lg overflow-hidden text-black bg-white">
          <div className="flex items-center justify-between px-4 py-2 border-b-2 border-black">
            <span className="font-heading font-extrabold tracking-tight">ROOFTOP PRINT</span>
            <span className="text-xs font-semibold px-2 py-0.5 border border-black rounded">NON-COD</span>
          </div>
          <div className="flex items-stretch border-b-2 border-black">
            <div className="flex-1 px-4 py-2">
              <p className="text-[10px] uppercase tracking-wide">Kurir</p>
              <p className="font-bold text-lg leading-tight">{order.shipping.courier}</p>
              <p className="text-xs">{order.shipping.service} · est. {order.shipping.etd}</p>
            </div>
            <div className="px-4 py-2 border-l-2 border-black text-right min-w-[130px]">
              <p className="text-[10px] uppercase tracking-wide">No. Pesanan</p>
              <p className="font-mono font-bold">#{order.order_number}</p>
              <p className="text-[10px] uppercase tracking-wide mt-1">No. Resi</p>
              <p className="font-mono font-bold text-sm">{order.tracking_number || "—"}</p>
            </div>
          </div>
          <div className="px-4 py-2 border-b-2 border-black">
            <p className="text-[10px] uppercase tracking-wide font-bold">Penerima</p>
            <p className="font-bold">{order.shipping_address.recipient_name} · {order.shipping_address.phone}</p>
            <p className="text-sm leading-snug">{order.shipping_address.address}</p>
            <p className="text-sm">{order.shipping_address.city}, {order.shipping_address.province} {order.shipping_address.postal_code}</p>
          </div>
          <div className="px-4 py-2 border-b-2 border-black">
            <p className="text-[10px] uppercase tracking-wide font-bold">Pengirim</p>
            <p className="font-bold">{S.name} · {S.phone}</p>
            <p className="text-sm leading-snug">{S.address}</p>
          </div>
          <div className="px-4 py-2">
            <p className="text-[10px] uppercase tracking-wide font-bold mb-1">Isi Paket ({(totalWeight / 1000).toFixed(1)} kg)</p>
            {order.items.map((it, idx) => {
              const vs = (it.variant_selections || []).map((v) => v.option_label).join(", ");
              return <p key={idx} className="text-sm">• {it.product_name}{vs ? ` (${vs})` : ""} × {it.quantity}</p>;
            })}
          </div>
        </div>

        <DialogFooter className="no-print">
          <Button onClick={() => window.print()} className="rounded-full bg-[#00133F] text-white" data-testid="print-label-btn">
            <Printer className="w-4 h-4 mr-2" /> Cetak Label
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OrderDetailDialog({ order, onClose }) {
  if (!order) return null;
  const subtotal = order.subtotal ?? order.items.reduce((s, it) => s + it.unit_price * it.quantity, 0);
  const shippingCost = order.shipping?.cost ?? 0;
  const discount = order.discount ?? 0;
  return (
    <Dialog open={!!order} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="order-detail-dialog">
        <DialogHeader>
          <DialogTitle>Detail Pesanan #{order.order_number}</DialogTitle>
          <DialogDescription>{order.user_email} · {new Date(order.created_at).toLocaleString("id-ID")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {order.items.map((it, idx) => {
            const vs = (it.variant_selections || []).map((v) => `${v.group_name}: ${v.option_label}`).join(" · ");
            const files = (it.design_files && it.design_files.length) ? it.design_files : (it.design_file_id ? [{ file_id: it.design_file_id, note: "" }] : []);
            return (
              <div key={idx} className="flex gap-3 rounded-xl border border-slate-200 p-3" data-testid={`detail-item-${idx}`}>
                {it.image_url && <img src={it.image_url} alt="" className="w-16 h-16 rounded-lg object-cover shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[#00133F]">{it.product_name}</p>
                  {vs && <p className="text-xs text-slate-500 mt-0.5">{vs}</p>}
                  <p className="text-sm text-slate-600 mt-1">{formatRupiah(it.unit_price)} × {it.quantity} = <span className="font-semibold text-[#00133F]">{formatRupiah(it.unit_price * it.quantity)}</span></p>
                  {it.notes && <p className="text-xs text-slate-400 mt-1 italic">Catatan: {it.notes}</p>}
                  {files.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {files.map((df, di) => (
                        <a key={di} href={`${api.defaults.baseURL}/files/${df.file_id}`} target="_blank" rel="noopener noreferrer" download
                          data-testid={`detail-design-${idx}-${di}`}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-slate-200 hover:border-[#F0005A] text-xs">
                          <Upload className="w-3 h-3 text-[#F0005A]" /> Desain{df.note ? ` — ${df.note}` : ` ${di + 1}`}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="rounded-xl bg-slate-50 p-4 mt-2 space-y-1.5 text-sm">
          <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="font-medium text-[#00133F]" data-testid="detail-subtotal">{formatRupiah(subtotal)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Ongkos Kirim ({order.shipping?.courier} {order.shipping?.service})</span><span className="font-medium text-[#00133F]" data-testid="detail-shipping">{shippingCost === 0 ? "Ambil di Toko / Gratis" : formatRupiah(shippingCost)}</span></div>
          {discount > 0 && <div className="flex justify-between text-[#F0005A]"><span>Diskon</span><span data-testid="detail-discount">- {formatRupiah(discount)}</span></div>}
          <div className="flex justify-between border-t border-slate-200 pt-2 mt-1"><span className="font-semibold text-[#00133F]">Total Pembayaran</span><span className="font-bold text-[#F0005A] text-base" data-testid="detail-total">{formatRupiah(order.total)}</span></div>
        </div>

        <div className="rounded-xl border border-slate-200 p-3 mt-2 text-sm">
          <p className="font-semibold text-[#00133F] mb-1">Alamat Pengiriman</p>
          <p className="text-slate-600">{order.shipping_address.recipient_name} · {order.shipping_address.phone}</p>
          <p className="text-slate-600">{order.shipping_address.address}, {order.shipping_address.city}, {order.shipping_address.province} {order.shipping_address.postal_code}</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ResiImageUpload({ order, onResiImage }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [viewUrl, setViewUrl] = useState("");
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!["image/jpeg", "image/png"].includes(f.type)) { toast.error("Gunakan file JPG atau PNG"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await onResiImage(order.id, up.data.file_id);
    } catch { toast.error("Gagal mengunggah bukti resi"); } finally { setBusy(false); }
  };
  const view = async () => {
    try {
      const r = await api.get(`/orders/${order.id}/resi-image`, { responseType: "blob" });
      setViewUrl(URL.createObjectURL(r.data));
    } catch { toast.error("Gagal memuat bukti resi"); }
  };
  const close = () => { URL.revokeObjectURL(viewUrl); setViewUrl(""); };
  return (
    <div className="flex items-center gap-2">
      <Button size="sm" variant="outline" onClick={() => ref.current?.click()} disabled={busy} className="rounded-full" data-testid={`upload-resi-image-${order.order_number}`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ImagePlus className="w-4 h-4 mr-1.5" /> {order.resi_image_file_id ? "Ganti Foto Resi" : "Upload Foto Resi"}</>}
      </Button>
      {order.resi_image_file_id && (
        <Button size="sm" variant="ghost" onClick={view} className="rounded-full text-[#F0005A]" data-testid={`view-resi-image-${order.order_number}`}><Eye className="w-4 h-4 mr-1" /> Lihat</Button>
      )}
      <input ref={ref} type="file" accept="image/jpeg,image/png" className="hidden" onChange={pick} data-testid={`resi-image-input-${order.order_number}`} />
      <Dialog open={!!viewUrl} onOpenChange={(v) => { if (!v) close(); }}>
        <DialogContent className="max-w-3xl" data-testid="admin-resi-dialog">
          <DialogHeader><DialogTitle>Bukti Resi #{order.order_number}</DialogTitle><DialogDescription>{order.tracking_number || ""}</DialogDescription></DialogHeader>
          <img src={viewUrl} alt="Bukti resi" className="max-h-[70vh] mx-auto object-contain" />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OrderRow({ order, onStatus, onTracking, onResiImage, onLabel, onPayment, onDetail }) {
  const [resi, setResi] = useState(order.tracking_number || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!resi.trim()) { toast.error("Masukkan nomor resi"); return; }
    setSaving(true);
    await onTracking(order.id, resi.trim());
    setSaving(false);
  };
  return (
    <div data-testid={`admin-order-${order.order_number}`} className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="font-heading font-semibold text-[#00133F]">#{order.order_number}</span>
            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusOf(order.order_status).cls}`} data-testid={`order-status-badge-${order.order_number}`}>{statusOf(order.order_status).label}</span>
            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${order.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{order.payment_status === "paid" ? "Lunas" : "Belum bayar"}</span>
          </div>
          <p className="text-sm font-semibold text-[#00133F] mt-1" data-testid={`order-customer-${order.order_number}`}>{order.shipping_address.recipient_name}</p>
          <p className="text-xs text-slate-500 mt-0.5">{order.user_email} · {order.shipping_address.city}</p>
          <p className="text-sm text-slate-400 mt-0.5">{order.items.map((it) => { const vs = (it.variant_selections || []).map((v) => v.option_label).join("/"); return `${it.product_name}${vs ? " (" + vs + ")" : ""} ×${it.quantity}`; }).join(", ")}</p>
          <p className="text-xs text-slate-500 mt-0.5" data-testid={`order-courier-${order.order_number}`}>Kurir: <span className="font-medium text-[#00133F]">{order.shipping?.courier} {order.shipping?.service}</span></p>
          <p className="text-xs text-slate-400 mt-0.5">{new Date(order.created_at).toLocaleString("id-ID")}</p>
        </div>
        <div className="text-right">
          <p className="font-bold text-[#F0005A] mb-2">{formatRupiah(order.total)}</p>
          <Select value={order.order_status} onValueChange={(v) => onStatus(order.id, v)}>
            <SelectTrigger className={`rounded-xl w-52 font-medium border-0 ${statusOf(order.order_status).cls}`} data-testid={`status-select-${order.order_number}`}><SelectValue /></SelectTrigger>
            <SelectContent>{ORDER_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
          {order.payment_status !== "paid" && order.order_status !== "dibatalkan" && (
            <Button size="sm" onClick={() => onPayment(order.id)} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white mt-2 w-52" data-testid={`mark-paid-${order.order_number}`}>
              <CheckCircle2 className="w-4 h-4 mr-1.5" /> Tandai Lunas
            </Button>
          )}
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-[240px]">
          <Truck className="w-4 h-4 text-slate-400" />
          <Input value={resi} onChange={(e) => setResi(e.target.value)} placeholder="Masukkan nomor resi pengiriman"
            className="rounded-xl h-9" data-testid={`resi-input-${order.order_number}`} />
          <Button size="sm" onClick={save} disabled={saving} className="rounded-full bg-[#00133F] text-white shrink-0" data-testid={`save-resi-${order.order_number}`}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Resi"}
          </Button>
        </div>
        {["dikirim", "selesai"].includes(order.order_status) && <ResiImageUpload order={order} onResiImage={onResiImage} />}
        <Button size="sm" variant="outline" onClick={() => onDetail(order)} className="rounded-full" data-testid={`order-detail-open-${order.order_number}`}>
          <ShoppingBag className="w-4 h-4 mr-1.5" /> Detail
        </Button>
        <Button size="sm" variant="outline" onClick={() => onLabel(order)} className="rounded-full" data-testid={`print-label-open-${order.order_number}`}>
          <Printer className="w-4 h-4 mr-1.5" /> Cetak Label
        </Button>
      </div>

      {order.items.some((it) => (it.design_files && it.design_files.length) || it.design_file_id) && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <p className="text-xs font-semibold text-[#00133F] mb-2">File Desain Konsumen (untuk produksi)</p>
          <div className="flex flex-wrap gap-2">
            {order.items.flatMap((it, ii) => {
              const files = (it.design_files && it.design_files.length) ? it.design_files : (it.design_file_id ? [{ file_id: it.design_file_id, note: "" }] : []);
              return files.map((df, di) => (
                <a key={`${ii}-${di}`} href={`${api.defaults.baseURL}/files/${df.file_id}`} download
                  target="_blank" rel="noopener noreferrer" data-testid={`download-design-${order.order_number}-${ii}-${di}`}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 hover:border-[#F0005A] hover:bg-[#F0005A]/5 transition-colors text-xs">
                  <img src={`${api.defaults.baseURL}/files/${df.file_id}`} alt="" className="w-6 h-6 rounded object-cover" />
                  <span className="font-medium text-[#00133F] max-w-[160px] truncate">{it.product_name}{df.note ? ` — ${df.note}` : ""}</span>
                  <Download className="w-3.5 h-3.5 text-[#F0005A]" />
                </a>
              ));
            })}
          </div>
        </div>
      )}
    </div>
  );
}

const EMPTY_POST = { title: "", excerpt: "", content: "", cover_image: "", published: true };

function BlogFormDialog({ open, onOpenChange, post, onSaved }) {
  const editing = !!post;
  const [form, setForm] = useState(EMPTY_POST);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm(post ? { ...EMPTY_POST, ...post } : EMPTY_POST); }, [open, post]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.title || !form.content) { toast.error("Judul dan isi artikel wajib diisi"); return; }
    setSaving(true);
    try {
      const payload = { title: form.title, excerpt: form.excerpt, content: form.content, cover_image: form.cover_image, published: form.published };
      if (editing) await api.put(`/admin/blog/${post.id}`, payload);
      else await api.post("/admin/blog", payload);
      toast.success(editing ? "Artikel diperbarui" : "Artikel ditambahkan");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error("Gagal menyimpan artikel"); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Artikel" : "Tulis Artikel Baru"}</DialogTitle>
          <DialogDescription>Isi judul, ringkasan, dan isi artikel. Gambar sampul opsional.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div><Label className="text-sm">Judul</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1.5 rounded-xl" data-testid="blog-title-input" /></div>
          <div><Label className="text-sm">Ringkasan (excerpt)</Label><Textarea value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} className="mt-1.5 rounded-xl" data-testid="blog-excerpt-input" /></div>
          <div><Label className="text-sm">Isi Artikel</Label><Textarea value={form.content} onChange={(e) => set("content", e.target.value)} className="mt-1.5 rounded-xl min-h-40" data-testid="blog-content-input" /></div>
          <div><Label className="text-sm">Gambar Sampul</Label><div className="mt-1.5"><ImageUploader value={form.cover_image} onChange={(v) => set("cover_image", v)} testid="blog-cover-input" /></div></div>
          <div className="flex items-center justify-between"><Label className="text-sm">Terbitkan</Label><Switch checked={form.published} onCheckedChange={(v) => set("published", v)} /></div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} className="rounded-full bg-[#F0005A] text-white" data-testid="save-blog-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Artikel"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BlogManager() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => { setLoading(true); api.get("/admin/blog").then((r) => setPosts(r.data)).finally(() => setLoading(false)); }, []);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => { if (!window.confirm("Hapus artikel ini?")) return; try { await api.delete(`/admin/blog/${id}`); toast.success("Artikel dihapus"); load(); } catch (e) { toast.error("Gagal menghapus"); } };
  return (
    <div>
      <div className="flex justify-end mb-5">
        <Button onClick={() => { setEditing(null); setOpen(true); }} className="rounded-full bg-[#00133F] text-white" data-testid="add-blog-btn"><Plus className="w-4 h-4 mr-2" /> Tulis Artikel</Button>
      </div>
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#F0005A]" /></div>
      ) : posts.length === 0 ? (
        <p className="text-slate-500 text-center py-10">Belum ada artikel.</p>
      ) : (
        <div className="space-y-3">
          {posts.map((p) => (
            <div key={p.id} data-testid={`admin-blog-${p.slug}`} className="rounded-2xl border border-slate-200 bg-white p-4 flex gap-4 items-center">
              {p.cover_image && <img src={p.cover_image} alt="" className="w-20 h-16 rounded-lg object-cover" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-heading font-semibold text-[#00133F] truncate">{p.title}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${p.published ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{p.published ? "Terbit" : "Draf"}</span>
                </div>
                <p className="text-xs text-slate-500 mt-1 line-clamp-1">{p.excerpt}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => { setEditing(p); setOpen(true); }} data-testid={`edit-blog-${p.slug}`}><Pencil className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="ghost" className="rounded-full text-red-500" onClick={() => del(p.id)} data-testid={`delete-blog-${p.slug}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <BlogFormDialog open={open} onOpenChange={setOpen} post={editing} onSaved={load} />
    </div>
  );
}

function PageContentManager() {
  const [logo, setLogo] = useState(null);
  const [about, setAbout] = useState(null);
  const [clients, setClients] = useState(null);
  const [busy, setBusy] = useState("");
  useEffect(() => {
    api.get("/settings/logo").then((r) => setLogo(r.data));
    api.get("/settings/about").then((r) => setAbout(r.data));
    api.get("/settings/clients").then((r) => setClients(r.data));
  }, []);

  const saveLogo = async () => { setBusy("logo"); try { await api.put("/admin/settings/logo", logo); toast.success("Logo disimpan"); } catch (e) { toast.error("Gagal"); } finally { setBusy(""); } };
  const saveAbout = async () => { if (!about.title || !about.body) { toast.error("Judul & isi wajib"); return; } setBusy("about"); try { await api.put("/admin/settings/about", about); toast.success("Halaman Tentang disimpan"); } catch (e) { toast.error("Gagal"); } finally { setBusy(""); } };
  const saveClients = async () => { setBusy("clients"); try { const val = { clients: clients.clients.filter((c) => c.logo_url) }; await api.put("/admin/settings/clients", val); toast.success("Logo brand disimpan"); } catch (e) { toast.error("Gagal"); } finally { setBusy(""); } };
  const addClient = () => setClients((c) => ({ clients: [...c.clients, { name: "", logo_url: "" }] }));
  const setClient = (i, k, v) => setClients((c) => ({ clients: c.clients.map((x, idx) => idx === i ? { ...x, [k]: v } : x) }));
  const removeClient = (i) => setClients((c) => ({ clients: c.clients.filter((_, idx) => idx !== i) }));

  if (!logo || !about || !clients) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#F0005A]" /></div>;

  return (
    <div className="space-y-6">
      {/* Logo */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h3 className="font-heading font-semibold text-[#00133F] mb-1">Logo Utama</h3>
        <p className="text-sm text-slate-500 mb-4">Unggah logo untuk mengganti logo teks bawaan. Kosongkan untuk memakai logo teks "ROOFTOP PRINT".</p>
        <ImageUploader value={logo.logo_url} onChange={(v) => setLogo({ logo_url: v })} testid="logo-image-input" />
        <div className="mt-4 flex gap-2">
          <Button onClick={saveLogo} disabled={busy === "logo"} className="rounded-full bg-[#F0005A] text-white" data-testid="save-logo-btn">{busy === "logo" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Logo"}</Button>
          {logo.logo_url && <Button variant="outline" className="rounded-full" onClick={() => setLogo({ logo_url: "" })} data-testid="reset-logo-btn">Hapus (pakai teks)</Button>}
        </div>
      </div>

      {/* About */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
        <h3 className="font-heading font-semibold text-[#00133F]">Halaman Tentang Kami</h3>
        <div><Label className="text-sm">Judul</Label><Input value={about.title} onChange={(e) => setAbout({ ...about, title: e.target.value })} className="mt-1.5 rounded-xl" data-testid="about-title-input" /></div>
        <div><Label className="text-sm">Isi (pisahkan paragraf dengan Enter)</Label><Textarea value={about.body} onChange={(e) => setAbout({ ...about, body: e.target.value })} className="mt-1.5 rounded-xl min-h-40" data-testid="about-body-input" /></div>
        <div><Label className="text-sm">Gambar</Label><div className="mt-1.5"><ImageUploader value={about.image_url} onChange={(v) => setAbout({ ...about, image_url: v })} testid="about-image-input" /></div></div>
        <Button onClick={saveAbout} disabled={busy === "about"} className="rounded-full bg-[#F0005A] text-white" data-testid="save-about-btn">{busy === "about" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Tentang Kami"}</Button>
      </div>

      {/* Clients */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-heading font-semibold text-[#00133F]">Logo "Telah Melayani"</h3>
            <p className="text-sm text-slate-500">Tambah atau ganti logo brand/instansi yang tampil di halaman utama.</p>
          </div>
          <Button size="sm" variant="outline" className="rounded-full" onClick={addClient} data-testid="add-client-btn"><Plus className="w-4 h-4 mr-1" /> Logo</Button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {clients.clients.map((c, i) => (
            <div key={i} className="rounded-xl border border-slate-200 p-4 flex gap-3 items-center" data-testid={`client-row-${i}`}>
              <div className="w-20 shrink-0"><ImageUploader value={c.logo_url} onChange={(v) => setClient(i, "logo_url", v)} testid={`client-logo-${i}`} /></div>
              <Input placeholder="Nama brand" value={c.name} onChange={(e) => setClient(i, "name", e.target.value)} className="rounded-xl" data-testid={`client-name-${i}`} />
              <button onClick={() => removeClient(i)} className="text-slate-400 hover:text-red-500 p-2"><X className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
        <Button onClick={saveClients} disabled={busy === "clients"} className="rounded-full bg-[#F0005A] text-white mt-4" data-testid="save-clients-btn">{busy === "clients" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Logo Brand"}</Button>
      </div>
    </div>
  );
}

function AddressManager({ onSaved }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const deb = useRef(null);
  useEffect(() => { api.get("/settings/sender").then((r) => { setS(r.data); setQ(r.data?.origin_label || ""); }); }, []);
  useEffect(() => {
    if (deb.current) clearTimeout(deb.current);
    if (q.trim().length < 3) { setResults([]); return; }
    deb.current = setTimeout(async () => {
      setSearching(true);
      try { const r = await api.get(`/shipping/destinations?search=${encodeURIComponent(q.trim())}`); setResults(r.data || []); setOpen(true); }
      catch (e) {} finally { setSearching(false); }
    }, 450);
    return () => deb.current && clearTimeout(deb.current);
  }, [q]);
  if (!s) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#F0005A]" /></div>;
  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const pickOrigin = (d) => { set("origin_id", d.id); set("origin_label", d.label); setQ(d.label); setOpen(false); };
  const save = async () => {
    if (!s.name || !s.phone || !s.address || !s.origin_id) { toast.error("Lengkapi semua data alamat & titik asal ongkir"); return; }
    setSaving(true);
    try {
      const payload = { name: s.name, phone: s.phone, address: s.address, origin_id: Number(s.origin_id), origin_label: s.origin_label || "" };
      const r = await api.put("/admin/settings/sender", payload);
      toast.success("Alamat pengirim disimpan & dijadikan titik asal ongkir");
      onSaved && onSaved(r.data);
    } catch (e) { toast.error("Gagal menyimpan alamat"); } finally { setSaving(false); }
  };
  return (
    <div className="max-w-2xl" data-testid="address-manager">
      <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
        <div className="flex items-center gap-2 mb-1"><MapPin className="w-5 h-5 text-[#F0005A]" /><h3 className="font-heading font-semibold text-[#00133F]">Alamat Pengirim Utama</h3></div>
        <p className="text-sm text-slate-500 -mt-2">Alamat ini dipakai sebagai titik asal (origin) ongkir RajaOngkir dan data pengirim pada label pengiriman.</p>
        <div><Label className="text-sm">Nama Pengirim</Label><Input value={s.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5 rounded-xl" data-testid="sender-name-input" /></div>
        <div><Label className="text-sm">No. Telepon</Label><Input value={s.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1.5 rounded-xl" data-testid="sender-phone-input" /></div>
        <div><Label className="text-sm">Alamat Lengkap</Label><Textarea value={s.address} onChange={(e) => set("address", e.target.value)} className="mt-1.5 rounded-xl min-h-20" data-testid="sender-address-input" /></div>
        <div className="relative">
          <Label className="text-sm">Titik Asal Ongkir (Kecamatan/Kota) — RajaOngkir</Label>
          <Input value={q} onChange={(e) => { setQ(e.target.value); }} placeholder="Ketik kecamatan/kota, mis. Astanaanyar Bandung"
            className="mt-1.5 rounded-xl" autoComplete="off" data-testid="sender-origin-search" />
          {searching && <Loader2 className="absolute right-3 top-9 w-4 h-4 text-slate-400 animate-spin" />}
          {open && results.length > 0 && (
            <div className="absolute z-20 mt-1 w-full max-h-56 overflow-auto rounded-xl border border-slate-200 bg-white shadow-lg" data-testid="sender-origin-results">
              {results.map((d) => (
                <button key={d.id} type="button" onClick={() => pickOrigin(d)} data-testid={`sender-origin-option-${d.id}`}
                  className="w-full text-left px-3 py-2.5 text-sm hover:bg-[#F0005A]/5 border-b border-slate-50 last:border-0 text-[#00133F]">{d.label}</button>
              ))}
            </div>
          )}
          {open && !searching && results.length === 0 && q.trim().length >= 3 && (
            <p className="text-xs text-slate-400 mt-1.5" data-testid="sender-origin-empty">Tujuan tidak ditemukan. Coba kata kunci lain.</p>
          )}
          {s.origin_id ? <p className="text-xs text-emerald-600 mt-1.5">Origin aktif: {s.origin_label} (ID {s.origin_id})</p> : <p className="text-xs text-amber-600 mt-1.5">Belum ada titik asal dipilih.</p>}
        </div>
        <Button onClick={save} disabled={saving} className="rounded-full bg-[#F0005A] text-white mt-2" data-testid="save-sender-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Alamat"}
        </Button>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [tab, setTab] = useState("orders");
  const [labelOrder, setLabelOrder] = useState(null);
  const [labelOpen, setLabelOpen] = useState(false);
  const [detailOrder, setDetailOrder] = useState(null);
  const [orderFilter, setOrderFilter] = useState("all");
  const [statusTab, setStatusTab] = useState("all");
  const [sender, setSender] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { api.get("/settings/sender").then((r) => setSender(r.data)).catch(() => {}); }, []);

  const load = useCallback(async (initial = false) => {
    if (initial) setLoading(true);
    try {
      const [s, o, p] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/orders"), api.get("/products?include_inactive=true"),
      ]);
      setStats(s.data); setOrders(o.data); setProducts(p.data);
    } catch (e) { toast.error("Gagal memuat data"); } finally { if (initial) setLoading(false); }
  }, []);

  useEffect(() => { load(true); }, [load]);

  const updateStatus = async (orderId, status) => {
    try {
      await api.put(`/admin/orders/${orderId}/status`, { order_status: status });
      toast.success("Status diperbarui");
      setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, order_status: status } : o)));
    } catch (e) { toast.error("Gagal memperbarui status"); }
  };

  const markPaid = async (orderId) => {
    try {
      const res = await api.put(`/admin/orders/${orderId}/payment`, { payment_status: "paid" });
      setOrders((prev) => prev.map((o) => (o.id === orderId ? res.data : o)));
      toast.success("Pesanan ditandai Lunas · stok varian otomatis dikurangi");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menandai lunas"); }
  };

  const updateTracking = async (orderId, tracking_number) => {
    try {
      const res = await api.put(`/admin/orders/${orderId}/tracking`, { tracking_number });
      toast.success("Nomor resi disimpan & status jadi Dikirim");
      setOrders((prev) => prev.map((o) => (o.id === orderId ? res.data : o)));
    } catch (e) { toast.error("Gagal menyimpan resi"); }
  };

  const updateResiImage = async (orderId, file_id) => {
    const res = await api.put(`/admin/orders/${orderId}/resi-image`, { file_id });
    toast.success("Foto bukti resi tersimpan");
    setOrders((prev) => prev.map((o) => (o.id === orderId ? res.data : o)));
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Hapus produk ini?")) return;
    try { await api.delete(`/products/${id}`); toast.success("Produk dihapus"); load(); }
    catch (e) { toast.error("Gagal menghapus"); }
  };

  const openCreate = () => { setEditProduct(null); setDialogOpen(true); };
  const openEdit = (p) => { setEditProduct(p); setDialogOpen(true); };
  const openLabel = (o) => { setLabelOrder(o); setLabelOpen(true); };

  return (
    <div className="App">
      <div className="no-print"><Navbar /></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8">
          <p className="text-sm text-slate-500 uppercase tracking-wider">Panel Admin</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#00133F]">Dashboard ROOFTOP PRINT</h1>
        </div>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[#F0005A]" /></div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
              <StatCard icon={ShoppingBag} label="Total Pesanan" value={stats.total_orders} color="bg-[#00133F]" active={orderFilter === "all"} onClick={() => { setOrderFilter("all"); setTab("orders"); }} />
              <StatCard icon={Wallet} label="Pendapatan (Lunas)" value={formatRupiah(stats.total_revenue)} color="bg-[#F0005A]" active={false} onClick={() => navigate("/admin/laporan")} />
              <StatCard icon={Clock} label="Menunggu Bayar" value={stats.pending_payment} color="bg-amber-500" active={orderFilter === "pending"} onClick={() => { setOrderFilter("pending"); setTab("orders"); }} />
              <StatCard icon={CheckCircle2} label="Pesanan Lunas" value={stats.paid_orders} color="bg-emerald-500" active={orderFilter === "paidorders"} onClick={() => { setOrderFilter("paidorders"); setTab("orders"); }} />
            </div>

            <SalesReport />

            <Tabs value={tab} onValueChange={setTab}>
              <div className="flex items-center gap-2 mb-3">
                <Button onClick={() => window.open("/admin/invoice", "_blank")} className="rounded-full bg-[#00133F] hover:bg-[#00133F]/90 text-white" data-testid="open-invoice-btn"><FileText className="w-4 h-4 mr-1.5" /> Invoice</Button>
              </div>
              <TabsList className="rounded-full flex-wrap h-auto">
                <TabsTrigger value="orders" data-testid="tab-orders" className="rounded-full">Pesanan</TabsTrigger>
                <TabsTrigger value="products" data-testid="tab-products" className="rounded-full">Produk</TabsTrigger>
                <TabsTrigger value="blog" data-testid="tab-blog" className="rounded-full">Blog</TabsTrigger>
                <TabsTrigger value="pages" data-testid="tab-pages" className="rounded-full">Halaman</TabsTrigger>
                <TabsTrigger value="storefront" data-testid="tab-storefront" className="rounded-full">Dekorasi Toko</TabsTrigger>
                <TabsTrigger value="address" data-testid="tab-address" className="rounded-full">Alamat</TabsTrigger>
              </TabsList>

              <TabsContent value="orders" className="mt-6">
                <div className="flex flex-wrap gap-2 mb-5" data-testid="order-status-tabs">
                  {[{ value: "all", label: "Semua" }, ...ORDER_STATUSES].map((s) => {
                    const cnt = s.value === "all" ? orders.length : orders.filter((o) => o.order_status === s.value).length;
                    return (
                      <button key={s.value} onClick={() => setStatusTab(s.value)} data-testid={`order-tab-${s.value}`}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${statusTab === s.value ? "bg-[#00133F] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                        {s.label} <span className="ml-1 opacity-70">({cnt})</span>
                      </button>
                    );
                  })}
                </div>
                {(() => {
                  const filteredOrders = orders.filter((o) => {
                    const byStatus = statusTab === "all" || o.order_status === statusTab;
                    const byPay = orderFilter === "pending" ? o.payment_status === "pending"
                      : (orderFilter === "paid" || orderFilter === "paidorders") ? o.payment_status === "paid"
                      : true;
                    return byStatus && byPay;
                  });
                  return filteredOrders.length === 0 ? (
                    <p className="text-slate-500 text-center py-16">Tidak ada pesanan pada filter ini.</p>
                  ) : (
                    <div className="space-y-3">
                      {filteredOrders.map((o) => (
                        <OrderRow key={o.id} order={o} onStatus={updateStatus} onTracking={updateTracking} onResiImage={updateResiImage} onLabel={openLabel} onPayment={markPaid} onDetail={setDetailOrder} />
                      ))}
                    </div>
                  );
                })()}
              </TabsContent>

              <TabsContent value="products" className="mt-6">
                <div className="flex justify-end mb-5">
                  <Button onClick={openCreate} className="rounded-full bg-[#00133F] text-white" data-testid="add-product-btn"><Plus className="w-4 h-4 mr-2" /> Tambah Produk</Button>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  {products.map((p) => (
                    <div key={p.id} data-testid={`admin-product-${p.id}`} className="rounded-2xl border border-slate-200 bg-white p-5 flex gap-4">
                      <img src={p.image_url} alt="" className="w-24 h-24 rounded-xl object-cover" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="font-heading font-semibold text-[#00133F] truncate">{p.name}</h3>
                          <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${p.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{p.active ? "Aktif" : "Nonaktif"}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-[#F0005A] font-bold">{priceInfo(p).from ? "mulai " : ""}{formatRupiah(priceInfo(p).effective)}</p>
                          {p.best_seller && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#F0005A] bg-[#F0005A]/10 px-2 py-0.5 rounded-full"><Flame className="w-3 h-3" /> BEST SELLER</span>}
                        </div>
                        {p.variant_groups?.length > 0 && <p className="text-xs text-slate-400 mt-0.5">{p.variant_groups.length} kategori · {(p.variant_combinations || []).length} kombinasi · {p.weight}g</p>}
                        <p className="text-xs text-slate-500 mt-1 line-clamp-2">{p.description}</p>
                        <div className="mt-3 flex gap-2">
                          <Button size="sm" variant="outline" className="rounded-full" onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`}><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</Button>
                          <Button size="sm" variant="ghost" className="rounded-full text-red-500 hover:text-red-600" onClick={() => deleteProduct(p.id)} data-testid={`delete-product-${p.id}`}><Trash2 className="w-3.5 h-3.5 mr-1" /> Hapus</Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="blog" className="mt-6">
                <BlogManager />
              </TabsContent>

              <TabsContent value="pages" className="mt-6">
                <PageContentManager />
              </TabsContent>

              <TabsContent value="storefront" className="mt-6">
                <StorefrontManager />
                <CourierManager />
              </TabsContent>

              <TabsContent value="address" className="mt-6">
                <AddressManager onSaved={setSender} />
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>

      <ProductFormDialog open={dialogOpen} onOpenChange={setDialogOpen} product={editProduct} onSaved={load} />
      <ShippingLabelDialog order={labelOrder} open={labelOpen} onOpenChange={setLabelOpen} sender={sender} />
      <OrderDetailDialog order={detailOrder} onClose={() => setDetailOrder(null)} />
    </div>
  );
}
